import { cfgBaseCurrency, cfgBaseCurrencySymbol, cfgMegajackpotFeed } from '@infinitro/config';
const MEGAJACKPOT_BASE_URL = 'https://vip.fghjk.net/megajackpot/feed/jackpotdata';
const POLL_INTERVAL_MS = 30_000;
const MAX_POLL_INTERVAL_MS = 300_000;
export const defaultJackpotCurrency = {
    prefix: '$',
    suffix: '',
    grouping: ',',
    decimal: '.',
    precision: 1
};
const defaultJackpotAmounts = {
    USD: {
        // MEGA: $1,000,000
        mega: '$1,000,000',
        // MAJOR: $10,000
        major: '$10,000',
        // MINOR: $100
        minor: '$100',
        // MINI: $5
        mini: '$5'
    },
    TRY: {
        // MEGA: ₺40,000,000
        mega: '₺40,000,000',
        // MAJOR: ₺400,000
        major: '₺400,000',
        // MINOR: ₺4,000
        minor: '₺4,000',
        // MINI: ₺200
        mini: '₺200'
    },
    DEFAULT: {
        mega: '-',
        major: '-',
        minor: '-',
        mini: '-'
    }
};
let pollingConsumerCount = 0;
let isPollingActive = false;
let pollTimeoutId = null;
let pollingUrl = null;
let currentAbortController = null;
let consecutiveFailures = 0;
let hasSuccessfulFetch = false;
const currencyCodeToPrefix = (code) => {
    try {
        return new Intl.NumberFormat('en', { style: 'currency', currency: code })
            .formatToParts(1)
            .find((part) => part.type === 'currency')?.value;
    }
    catch {
        return undefined;
    }
};
export const getJackpotCurrencySetting = () => {
    const currencyCode = cfgMegajackpotFeed()?.currency;
    const baseCurrencySymbol = cfgBaseCurrencySymbol();
    if (!currencyCode && baseCurrencySymbol === '₺') {
        return {
            ...defaultJackpotCurrency,
            prefix: '',
            suffix: ' ₺',
            grouping: '.',
            decimal: ',',
            noDecimalAmount: 0
        };
    }
    const prefix = (currencyCode && currencyCodeToPrefix(currencyCode)) ||
        baseCurrencySymbol ||
        defaultJackpotCurrency.prefix;
    return {
        ...defaultJackpotCurrency,
        prefix
    };
};
function unicodeToChar(text) {
    return text.replace(/\\u([\dA-F]{4})/gi, (_, charCode) => String.fromCharCode(parseInt(charCode, 16)));
}
const formatOptionalJackpotValue = (value, currency) => (value == null ? '' : formatJackpotCurrency(value, currency));
export function formatJackpotCurrency(value, currency = defaultJackpotCurrency) {
    const prefix = unicodeToChar(currency.prefix);
    const suffix = unicodeToChar(currency.suffix);
    const grouping = unicodeToChar(currency.grouping);
    const decimal = unicodeToChar(currency.decimal);
    const { precision } = currency;
    const p2l = {
        1: 0,
        100: 2,
        100000: 5
    };
    const isNegative = value < 0;
    const internalNumber = Math.abs(value).toString().padStart(3, '0');
    const showKilo = precision === 100000 && internalNumber.length >= 6;
    const prec = showKilo ? p2l[precision] : 2;
    const str = internalNumber.substring(0, internalNumber.length - prec);
    const decimals = internalNumber.substr(internalNumber.length - prec, 2);
    let i = str.length;
    let result = '';
    while ((i -= 3) > 0) {
        result = `${grouping}${str.substr(i, 3)}${result}`;
    }
    const noDecimalAmount = currency.noDecimalAmount ?? -1;
    const showDecimals = decimals !== '00' && (noDecimalAmount === -1 || Math.abs(value) < noDecimalAmount);
    const decimalSuffix = showDecimals ? decimal + decimals : '';
    return `${isNegative ? '-' : ''}${prefix}${str.substr(0, i + 3)}${result}${decimalSuffix}${showKilo ? 'k' : ''}${suffix}`;
}
const getDefaultJackpotAmounts = (currency) => defaultJackpotAmounts[currency] ??
    defaultJackpotAmounts.DEFAULT;
export const jackpotAmounts = $state(getDefaultJackpotAmounts(cfgBaseCurrency()));
export const jackpotMEGA = $state({ value: jackpotAmounts.mega });
const getMegajackpotFeedUrl = () => {
    const feed = cfgMegajackpotFeed();
    if (!feed) {
        return null;
    }
    const params = new URLSearchParams({
        operator: feed.operator,
        hash: feed.hash
    });
    if (feed.currency) {
        params.set('currency', feed.currency);
    }
    return `${MEGAJACKPOT_BASE_URL}?${params.toString()}`;
};
const resetJackpotAmounts = () => {
    const amounts = getDefaultJackpotAmounts(cfgBaseCurrency());
    jackpotAmounts.mega = amounts.mega;
    jackpotAmounts.major = amounts.major;
    jackpotAmounts.minor = amounts.minor;
    jackpotAmounts.mini = amounts.mini;
    jackpotMEGA.value = amounts.mega;
};
const isAbortError = (error) => error instanceof DOMException && error.name === 'AbortError';
const getPollDelay = (failures) => {
    if (failures === 0) {
        return POLL_INTERVAL_MS;
    }
    return Math.min(MAX_POLL_INTERVAL_MS, POLL_INTERVAL_MS * 2 ** failures);
};
const logMegajackpotFeedFailure = (phase, error, extra = {}) => {
    const feed = cfgMegajackpotFeed();
    console.warn('Megajackpot feed failed', {
        phase,
        operator: feed?.operator,
        currency: feed?.currency,
        status: extra.status,
        statusText: extra.statusText,
        consecutiveFailures,
        hasLiveData: hasSuccessfulFetch
    }, error);
};
const recordFeedSuccess = () => {
    consecutiveFailures = 0;
    hasSuccessfulFetch = true;
};
const recordFeedFailure = (phase, error, extra = {}) => {
    consecutiveFailures += 1;
    logMegajackpotFeedFailure(phase, error, extra);
    if (!hasSuccessfulFetch) {
        resetJackpotAmounts();
    }
};
const isMegajackpotJackpots = (value) => typeof value === 'object' && value !== null;
const applyJackpotData = (data) => {
    const jackpots = data?.jackpots;
    if (!isMegajackpotJackpots(jackpots)) {
        throw new Error('Megajackpot feed response missing jackpots object');
    }
    const currency = getJackpotCurrencySetting();
    jackpotAmounts.mega = formatOptionalJackpotValue(jackpots.MEGA_VALUE, currency);
    jackpotAmounts.major = formatOptionalJackpotValue(jackpots.MAJOR_VALUE, currency);
    jackpotAmounts.minor = formatOptionalJackpotValue(jackpots.MINOR_VALUE, currency);
    jackpotAmounts.mini = formatOptionalJackpotValue(jackpots.MINI_VALUE, currency);
    jackpotMEGA.value = jackpotAmounts.mega;
};
const fetchMegajackpotMega = async (url, signal) => {
    let res;
    try {
        res = await fetch(url, { signal });
    }
    catch (error) {
        if (isAbortError(error)) {
            return;
        }
        recordFeedFailure('network', error);
        return;
    }
    if (!res.ok) {
        recordFeedFailure('http', new Error(`${res.status} ${res.statusText}`), {
            status: res.status,
            statusText: res.statusText
        });
        return;
    }
    let data;
    try {
        data = await res.json();
    }
    catch (error) {
        if (isAbortError(error)) {
            return;
        }
        recordFeedFailure('json', error);
        return;
    }
    try {
        applyJackpotData(data);
        recordFeedSuccess();
    }
    catch (error) {
        recordFeedFailure('format', error);
    }
};
const stopPollingLoop = () => {
    isPollingActive = false;
    currentAbortController?.abort();
    currentAbortController = null;
    if (pollTimeoutId) {
        clearTimeout(pollTimeoutId);
        pollTimeoutId = null;
    }
    pollingUrl = null;
    consecutiveFailures = 0;
};
const scheduleNextPoll = (url) => {
    if (pollingConsumerCount === 0) {
        return;
    }
    pollTimeoutId = setTimeout(() => {
        pollTimeoutId = null;
        runPollCycle(url);
    }, getPollDelay(consecutiveFailures));
};
const runPollCycle = async (url) => {
    if (pollingConsumerCount === 0) {
        return;
    }
    const controller = new AbortController();
    currentAbortController = controller;
    try {
        await fetchMegajackpotMega(url, controller.signal);
    }
    finally {
        if (currentAbortController === controller) {
            currentAbortController = null;
        }
        if (pollingConsumerCount > 0) {
            scheduleNextPoll(url);
        }
    }
};
const startPollingLoop = (url) => {
    runPollCycle(url);
};
export const startMegajackpotPolling = () => {
    const url = getMegajackpotFeedUrl();
    if (!url) {
        console.warn('Megajackpot feed URL not found');
        return () => { };
    }
    let isActive = true;
    pollingConsumerCount += 1;
    if (!isPollingActive) {
        isPollingActive = true;
        pollingUrl = url;
        startPollingLoop(url);
    }
    else if (pollingUrl !== url) {
        console.warn('Megajackpot polling already active with a different feed URL');
    }
    return () => {
        if (!isActive)
            return;
        isActive = false;
        pollingConsumerCount = Math.max(0, pollingConsumerCount - 1);
        if (pollingConsumerCount === 0) {
            stopPollingLoop();
        }
    };
};
